import { KibbutzNlp, Page } from '@testsigmainc/models';
import {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from 'react-query';
import api from '../../../api/instance';
import { PrivateURLConstants } from '@constants/private-url.constant';

const nameSpace = PrivateURLConstants.KIBBUTZ + '/nlps';
const DEFAULT_PAGE_SIZE = 2000;

interface UseFindAllArgs
  extends Omit<UseQueryOptions<KibbutzNlp[], Error>, 'queryKey' | 'queryFn'> {
  additionalQueryKey?: QueryKey | string | number | boolean | null | undefined;
}

let fetchAllKibbutzTemplatesPromise: Promise<KibbutzNlp[]> | null = null;

const fetchAllKibbutzTemplates = async (): Promise<KibbutzNlp[]> => {
  if (fetchAllKibbutzTemplatesPromise) {
    return fetchAllKibbutzTemplatesPromise;
  }

  fetchAllKibbutzTemplatesPromise = (async () => {
    const templates: KibbutzNlp[] = [];
    let pageNumber = 0;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await api
        .get(nameSpace, { page: pageNumber, size: DEFAULT_PAGE_SIZE })
        .then((res) =>
          new Page<KibbutzNlp>().deserialize(res, KibbutzNlp as any),
        );

      templates.push(...response.content);

      const totalPages = response.totalPages ?? Number.MAX_SAFE_INTEGER;
      const reachedLastPage =
        response.last ||
        response.content.length === 0 ||
        pageNumber + 1 >= totalPages;

      hasNextPage = !reachedLastPage;
      pageNumber += 1;
    }

    return templates;
  })();

  try {
    return await fetchAllKibbutzTemplatesPromise;
  } finally {
    fetchAllKibbutzTemplatesPromise = null;
  }
};

const findAll = (query?: string): Promise<Page<KibbutzNlp>> => {
  return new Promise((resolve, reject) => {
    api
      .get(nameSpace, { query })
      .then((res) =>
        resolve(new Page<KibbutzNlp>().deserialize(res, KibbutzNlp as any)),
      )
      .catch((e) => reject(e));
  });
};

const findAllWithoutPagination = (): Promise<KibbutzNlp[]> => {
  return fetchAllKibbutzTemplates();
};

const useFindAll = ({
  additionalQueryKey = [],
  ...options
}: UseFindAllArgs = {}): UseQueryResult<KibbutzNlp[], Error> => {
  const baseKey: QueryKey = ['findAllKibbutzTemplates'];
  const normalizedAdditionalKey = Array.isArray(additionalQueryKey)
    ? additionalQueryKey
    : [additionalQueryKey];

  const queryKey: QueryKey = [...baseKey, ...normalizedAdditionalKey];

  return useQuery<KibbutzNlp[], Error>({
    queryKey,
    queryFn: () => fetchAllKibbutzTemplates(),
    staleTime: Infinity,
    cacheTime: Infinity,
    ...options,
  });
};

const KibbutzNlpService = {
  findAll,
  findAllWithoutPagination,
  useFindAll,
};

export default KibbutzNlpService;
